import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import * as yup from 'yup';
import { InputDate, InputNumber, InputSelect } from '~/components/NovosInputs';
import { SelectType } from '~/components/NovosInputs/InputAsyncSelect/protocols';
import { useEmissaoNFE } from '~/pages/EmissaoNFE/EmissaoNFEContext';
import { CalculosDaTabela } from '~/pages/EmissaoNFE/functions/Calculos';
import { CuponsReferenciados, Pdv } from '~/pages/EmissaoNFE/protocols';
import api from '~/services/api';

import { CustomButtonNovo } from '~/components/Buttons/CustomButtonNovo';
import { ResponsiveModal } from './styles';
import { alimentaDadosIBSCBSCliente } from '~/components/NovosInputs/InputIbsCbs/functions/alimentaDadosIBSCBSCliente';
import {
  formatCurrencyBR,
  moneyFormat,
  strPadZeroL,
  toNumberOrZero,
} from '~/utils/functions';

const MySwal = withReactContent(Swal);

interface ModalProps {
  handleModal: {
    showModal: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  };
}

const schema = yup.object().shape({
  dta_cupom: yup.string().required(),
  num_cupom: yup.number().required(),
  pdv: yup.object().shape({
    label: yup.string().required(),
    value: yup.number().required(),
  }),
});

export const Modal: React.FC<ModalProps> = (props) => {
  const titleId = 'inserir-cupom-title';
  const [loadingCupom, setLoadingCupom] = useState(false);

  const { handleModal } = props;
  const {
    codLoja,
    cuponsReferenciados,
    setCuponsReferenciados,
    loja,
    parceiro,
    produtos,
    setProdutos,
    formNFE: { getValues: getFormNFEValues },
    formItens: { reset: resetFormItens },
    formDadosEntrega: { getValues: getValuesDadosEntrega },
  } = useEmissaoNFE();
  /**
   * Form NFE
   */
  const { register, handleSubmit, control, setValue, getValues, formState } =
    useForm({
      resolver: yupResolver(schema),
      reValidateMode: 'onBlur',
    });

  const [pdvs, setPdvs] = useState<SelectType[]>([]);
  const [inputPdv, setInputPdv] = useState<number>();

  const getCupomData = useCallback(async () => {
    const dataEmissao = getValues('dta_cupom');
    const numCupom = getValues('num_cupom');
    const { num_pdv } = getValues('pdv');

    try {
      const { data } = await api.get('/cupom', {
        params: {
          cod_loja: codLoja,
          dta_emissao: dataEmissao,
          num_cupom: numCupom,
          num_pdv,
        },
      });
      return data.data;
    } catch (error: any) {
      if (error.data && error.data.message) {
        toast.error(error.data.message);
        return;
      }
      toast.error(String(error));
      return [];
    }
  }, [codLoja, getValues]);

  const getCupomItems = useCallback(async () => {
    const dtaCupom = getValues('dta_cupom');
    const numCupom = getValues('num_cupom');
    const { num_pdv } = getValues('pdv');

    try {
      const { data } = await api.get('/cupom/items', {
        params: {
          cod_loja: codLoja,
          num_cupom: numCupom,
          num_pdv,
          dta_cupom: dtaCupom,
        },
      });
      return data.data;
    } catch (error: any) {
      if (error.data && error.data.message) {
        toast.error(error.data.message);
        return;
      }
      toast.error(String(error));
      return [];
    }
  }, [codLoja, getValues]);

  const handleCuponsReferenciados = useCallback(
    (itensDoCupom: string[], cod_seq_cupom: string, num_cupom: string) => {
      function verificaItensDoCupomReferenciado(cupom: CuponsReferenciados) {
        /**
         * Nesse método é efetuado um merge dos itens já referenciados a NF com os itens selecionados na tabela.
         * Após o merge é efetuado um reduce com a finalidade de remover itens duplicados.
         * Após o reduce os itens são atualizados na NF referenciada.
         */
        const array = [...cupom.itens, ...itensDoCupom].reduce(
          (unique: any, item: any) => {
            return unique.includes(item) ? unique : [...unique, item];
          },
          [],
        );
        setCuponsReferenciados((prev) =>
          prev.map((item) => {
            if (item.cod_seq_cupom === cupom.cod_seq_cupom) {
              item.itens = array;
            }
            return item;
          }),
        );
      }
      /**
       * Verifica se NF já está referenciada
       */
      const nfExists = cuponsReferenciados.find(
        (nf) => nf.cod_seq_cupom === cod_seq_cupom,
      );
      if (nfExists) {
        /**
         * Caso a NF já esteja referenciada o método verificaItensDoCupomReferenciado será chamado
         * com a finalidade de atualizar a lista dos itens vinculados a essa NF.
         */
        verificaItensDoCupomReferenciado(nfExists);
        return;
      }
      /**
       * Caso a NF ainda não esteja referenciada, ela será adicionada a lista de NFs referenciadas.
       */
      setCuponsReferenciados((prev) => [
        ...prev,
        {
          cod_seq_cupom,
          num_cupom,
          itens: itensDoCupom,
        },
      ]);
    },
    [cuponsReferenciados, setCuponsReferenciados],
  );

  const confirmarReusoDeCupom = async (numNf: number): Promise<boolean> => {
    const result = await MySwal.fire({
      title: `Esse cupom já foi utilizado para a Nota Fiscal nº ${numNf}`,
      text: 'Deseja utilizá-lo novamente?',
      showCancelButton: true,
      confirmButtonColor: '#8850BF',
      cancelButtonColor: '#DE350B',
      confirmButtonText: 'Sim',
      cancelButtonText: 'Não',
    });
    return result.isConfirmed === true;
  };

  const calcularCfop = (
    ufLoja?: string,
    ufParceiro?: string,
  ): '5929' | '6929' =>
    ufLoja && ufParceiro && ufLoja === ufParceiro ? '5929' : '6929';

  const obterLocalConsumoIndex = (): number => {
    const rawLocalConsumo = getFormNFEValues('tipo_local_consumo')?.value;
    const parsed =
      typeof rawLocalConsumo === 'string'
        ? Number(rawLocalConsumo)
        : (rawLocalConsumo as number | undefined);
    return Number.isFinite(parsed) ? (parsed as number) : 0;
  };

  const processarIbsCbsParaItens = async ({
    itensParaProcessar,
  }: {
    itensParaProcessar: any[];
  }): Promise<any[]> => {
    const localConsumoIndex = obterLocalConsumoIndex();
    const rawCidadeEntrega =
      getValuesDadosEntrega('cod_cidade_entrega')?.value ?? null;

    const pairs = await alimentaDadosIBSCBSCliente({
      itensParaProcessar,
      ctx: {
        cbxLocalConsumoIndex: localConsumoIndex,
        loja: { codCidade: loja.cod_cidade, estado: loja.uf },
        empresa: loja.empresa,
        cliMestreCodCidade: parceiro.cod_cidade,
        cliCidadeCodCidade: rawCidadeEntrega,
      },
      isItemEditing: false,
    });

    return pairs;
  };

  const handleBuscaNf = handleSubmit(async (data) => {
    setLoadingCupom(true);
    try {
      const cupomJaExiste = cuponsReferenciados.some(
        (cupom) => cupom.num_cupom === data.num_cupom,
      );
      if (cupomJaExiste) {
        toast.warning('Cupom já incluso nessa NF');
        return;
      }

      const parceiroForm = getFormNFEValues('busca_parceiro') ?? {};
      const flg_orgao_publico = Boolean(parceiroForm?.flg_orgao_publico);

      const cupomData = await getCupomData();
      const infoCupom = cupomData?.[0];

      if (!infoCupom?.flg_existe) {
        toast.warning('Cupom não encontrado');
        return;
      }

      // Se já possui NF, confirmar reuso
      if (infoCupom.num_nf > 0) {
        const prosseguir = await confirmarReusoDeCupom(infoCupom.num_nf);
        if (!prosseguir) return;
      }

      // Buscar e calcular itens do cupom
      const cupomItems = await getCupomItems();
      if (!cupomItems) return;

      const cfop = calcularCfop(loja?.uf, parceiro?.des_uf);
      const formatedItems: any[] = await CalculosDaTabela({
        itens: cupomItems,
        flg_orgao_publico,
        tipo_regime: loja.tipo_regime,
        des_sigla: loja.uf,
        cfop,
        lastIndex: produtos.length,
      });

      const itensParaProcessar = [...formatedItems];

      const mapaIbsCbs = await processarIbsCbsParaItens({ itensParaProcessar });

      // Limite de itens
      const total = produtos.length + formatedItems.length;
      if (total > 500) {
        toast.warning('Limite de 500 itens excedido.');
        return;
      }

      const ibscbsFormatted = mapaIbsCbs.map((itemibs: any) => ({
        ...itemibs,
        val_red_social: formatCurrencyBR(itemibs.val_red_social),
        per_aliq_ibs_est: moneyFormat(String(itemibs.per_aliq_ibs_est)),
        val_bc_ibs_cbs: moneyFormat(
          toNumberOrZero(itemibs.val_bc_ibs_cbs).toFixed(2),
        ),
        val_cbs: formatCurrencyBR(itemibs.val_cbs?.toFixed(2)),
        val_ibs_est: formatCurrencyBR(itemibs.val_ibs_est),
        val_ibs_mun: moneyFormat(String(itemibs.val_ibs_mun)),
        per_red_ibs: moneyFormat(String(itemibs.per_red_ibs)),
        per_aliq_cbs: moneyFormat(String(itemibs.per_aliq_cbs)),
        per_red_cbs: moneyFormat(String(itemibs.per_red_cbs)),
        per_aliq_ibs_mun: moneyFormat(String(itemibs.per_aliq_ibs_mun)),
        cod_beneficio_fiscal: strPadZeroL(8, itemibs.cod_beneficio_fiscal),
      }));

      // Atualiza produtos
      setProdutos((prev) => [...prev, ...ibscbsFormatted]);

      // Marca cupom referenciado
      const listaDeItensPorCupomReferenciado = formatedItems.map(
        (i) => i.cod_produto,
      );
      handleCuponsReferenciados(
        listaDeItensPorCupomReferenciado,
        infoCupom.cod_seq_cupom,
        infoCupom.num_cupom,
      );

      // Limpa UI de itens e fecha modal somente após sucesso
      resetFormItens();
      handleModal.setShowModal(false);
    } finally {
      setLoadingCupom(false);
    }
  });

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/pdvs/${codLoja}`);

      if (!data.success) {
        toast.error(
          'Não foi possível carregar os PDVs para a Loja selecionada.',
        );
        return;
      }
      const options = data.data.map((pdv: Pdv) => {
        return {
          value: pdv.num_pdv,
          label: String(pdv.num_pdv),
          ...pdv,
        };
      });

      setPdvs(options);
    })();
  }, [codLoja]);

  return (
    <ResponsiveModal
      show={handleModal.showModal}
      onHide={() => handleModal.setShowModal(false)}
      centered
      aria-labelledby={titleId}
      dialogClassName="responsive-modal"
    >
      <ResponsiveModal.Header>
        <ResponsiveModal.Title id={titleId}>
          Inserir Cupom
        </ResponsiveModal.Title>
      </ResponsiveModal.Header>

      <ResponsiveModal.Body>
        <Row className="form-grid">
          <Col xs={12} sm={6} lg={3}>
            <InputDate
              register={register}
              label="Emissão"
              name="dta_cupom"
              control={control}
              isError={!!formState.errors.dta_cupom}
              aria-invalid={!!formState.errors.dta_cupom}
            />
          </Col>

          <Col xs={12} sm={6} lg={3}>
            <InputNumber
              label="Número"
              max={100000}
              maxLength={50}
              placeholder="0"
              name="num_cupom"
              register={register}
              disabled={false}
              isError={!!formState.errors.num_cupom}
              aria-invalid={!!formState.errors.num_cupom}
            />
          </Col>

          <Col xs={12} sm={6} lg={3}>
            <InputSelect
              label="Caixa"
              maxLength={50}
              placeholder="Selecione..."
              value={inputPdv}
              /** usar onChange padrão (evita onChangeCapture) */
              onChange={(event: any) => {
                const numPdv = event?.target?.value;
                setInputPdv(Number(numPdv));
              }}
              onBlur={() => {
                const selected = pdvs.find((i: any) => i.num_pdv === inputPdv);
                if (selected) {
                  setValue('pdv', selected);
                  setInputPdv(undefined);
                }
              }}
              name="pdv"
              register={register}
              isError={!!formState.errors.pdv}
              control={control}
              options={pdvs}
              changeSelected={(selected) => {
                setValue('pdv', selected);
                setInputPdv(undefined);
              }}
              aria-invalid={!!formState.errors.pdv}
            />
          </Col>

          {/* COLUNA DO BOTÃO */}
          <Col xs={12} sm={6} lg={3} className="btn-col">
            <CustomButtonNovo
              onClick={async () => {
                await handleBuscaNf();
              }}
              variant="confirm"
              showLoading={loadingCupom}
              disabled={loadingCupom}
              label="Adicionar"
              width="100%"
            />
          </Col>
        </Row>
      </ResponsiveModal.Body>
    </ResponsiveModal>
  );
};
